import { useState } from "react";
import { DropdownMenu } from "../DropDown/DropdownMenu";
import { DropdownMenuTrigger } from "../DropDown/DropdownMenuTrigger";
import { Button } from "../Button";
import { Check, ChevronRight } from "lucide-react";
import { DropdownMenuContent } from "../DropDown/DropdownMenuContent";
import { twMerge } from "tailwind-merge";

export interface ShadowPalleteProps {
    onChoose?: (value: ShadowItem) => void;
    className?: string;
}

export type ShadowItem = {
    key: string;
    label: string;
    twClass: string;
    cssValue: string;
};

const SHADOWS: ShadowItem[] = [
    {
        key: "shadow-none",
        label: "Sem sombra",
        twClass: "shadow-none",
        cssValue: "none",
    },
    {
        key: "shadow-subtle",
        label: "Sutil",
        twClass: "shadow-[rgba(0,0,0,0.15)_1.95px_1.95px_2.6px]",
        cssValue: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
    },
    {
        key: "shadow-clean",
        label: "Limpa",
        twClass: "shadow-[rgba(149,157,165,0.2)_0px_8px_24px]",
        cssValue: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
    },
    {
        key: "shadow-layered",
        label: "Em camadas",
        twClass:
            "shadow-[rgba(0,0,0,0.05)_0px_6px_24px_0px,rgba(0,0,0,0.08)_0px_0px_0px_1px]",
        cssValue:
            "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
    },
    {
        key: "shadow-multi-layer",
        label: "Multi-camadas",
        twClass:
            "shadow-[rgba(14,63,126,0.04)_0px_0px_0px_1px,rgba(42,51,69,0.04)_0px_1px_1px_-0.5px,rgba(42,51,70,0.04)_0px_3px_3px_-1.5px,rgba(42,51,70,0.04)_0px_6px_6px_-3px,rgba(14,63,126,0.04)_0px_12px_12px_-6px,rgba(14,63,126,0.04)_0px_24px_24px_-12px]",
        cssValue:
            "rgba(14, 63, 126, 0.04) 0px 0px 0px 1px, rgba(42, 51, 69, 0.04) 0px 1px 1px -0.5px, rgba(42, 51, 70, 0.04) 0px 3px 3px -1.5px, rgba(42, 51, 70, 0.04) 0px 6px 6px -3px, rgba(14, 63, 126, 0.04) 0px 12px 12px -6px, rgba(14, 63, 126, 0.04) 0px 24px 24px -12px",
    },
    {
        key: "shadow-smooth",
        label: "Suave",
        twClass: "shadow-[rgba(0,0,0,0.1)_0px_10px_50px]",
        cssValue: "rgba(0, 0, 0, 0.1) 0px 10px 50px",
    },
    {
        key: "shadow-medium",
        label: "Média",
        twClass: "shadow-[rgba(0,0,0,0.15)_0px_5px_15px_0px]",
        cssValue: "rgba(0, 0, 0, 0.15) 0px 5px 15px 0px",
    },
    {
        key: "shadow-strong",
        label: "Forte",
        twClass: "shadow-[rgba(0,0,0,0.45)_0px_25px_20px_-20px]",
        cssValue: "rgba(0, 0, 0, 0.45) 0px 25px 20px -20px",
    },
    {
        key: "shadow-elegant",
        label: "Elegante",
        twClass:
            "shadow-[rgba(255,255,255,0.1)_0px_1px_1px_0px_inset,rgba(50,50,93,0.25)_0px_50px_100px_-20px,rgba(0,0,0,0.3)_0px_30px_60px_-30px]",
        cssValue:
            "rgba(255, 255, 255, 0.1) 0px 1px 1px 0px inset, rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.3) 0px 30px 60px -30px",
    },
    {
        key: "shadow-deep-inner",
        label: "Interna profunda",
        twClass:
            "shadow-[rgba(50,50,93,0.25)_0px_30px_60px_-12px_inset,rgba(0,0,0,0.3)_0px_18px_36px_-18px_inset]",
        cssValue:
            "rgba(50, 50, 93, 0.25) 0px 30px 60px -12px inset, rgba(0, 0, 0, 0.3) 0px 18px 36px -18px inset",
    },
];


const ShadowPallete = ({ onChoose, className }: ShadowPalleteProps) => {
    const [selected, setSelected] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const handlePick = (item: ShadowItem) => {
        setSelected(item.key);
        onChoose?.(item);
    };

    return (
        <>
            <DropdownMenu isOpen={isOpen} setIsOpen={setIsOpen}>
                <DropdownMenuTrigger>
                    <Button variant='navigable'>
                        Escolher Sombra
                        <ChevronRight size={16} className="text-gray-500" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" side="right" sideOffset={20} className="min-h-[112px] w-[200px]">
                    <div className={className}>
                        <div className="flex flex-wrap gap-2">
                            {SHADOWS.map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    title={`${item.key} → ${item.cssValue}`}
                                    onClick={() => handlePick(item)}
                                    className={twMerge(
                                        "relative size-24 rounded-md bg-white",
                                        "border transition",
                                        item.twClass,
                                        "flex items-center justify-center text-[11px] font-medium text-gray-600"
                                    )}
                                >
                                    {item.label}

                                    {selected === item.key && (
                                        <Check
                                            size={14}
                                            className="absolute top-1.5 right-1.5 text-purple-800 bg-white/80 rounded-full p-[1px]"
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </DropdownMenuContent>
            </DropdownMenu>

        </>
    );
};

export default ShadowPallete;
